import { useCallback, useContext } from "react";
import { TransactionsPairForReconciliation } from "@common/contracts/reconciliation.contract";
import { FinancialAccount } from "@common/types/financialAccount";
import { useReconcileTransactionsMutation } from "@api/reconciliation.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ReconciliationContext } from "./Reconciliation.context";
import { buildReconcilePayload } from "./workflowHelpers";

interface UseFinishReconciliationParams {
    pairs: TransactionsPairForReconciliation[];
    financialAccount: FinancialAccount;
}

export function useFinishReconciliation({
    pairs,
    financialAccount,
}: UseFinishReconciliationParams) {
    const {
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIdsSet,
        financialTransactionIdsIgnoringDateChanges,
        financialTransactionsFromPreviousPeriodIds,
        checkMerges,
        setIsReconciliationComplete,
    } = useContext(ReconciliationContext);
    const toaster = useToaster();
    const { mutate: reconcile, isPending: isFinishing } =
        useReconcileTransactionsMutation(() => {
            toaster.toast("Account reconciled");
            setIsReconciliationComplete(true);
        });

    const finish = useCallback(() => {
        reconcile(
            buildReconcilePayload({
                financialAccountId: financialAccount.id,
                pairs,
                financialTransactionsToRemoveIds,
                statementTransactionsToAddIdsSet,
                financialTransactionIdsIgnoringDateChanges,
                financialTransactionsFromPreviousPeriodIds,
                checkMerges,
            }),
        );
    }, [
        financialAccount.id,
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIdsSet,
        financialTransactionsFromPreviousPeriodIds,
        financialTransactionIdsIgnoringDateChanges,
        checkMerges,
        reconcile,
        pairs,
    ]);

    return { finish, isFinishing };
}
